import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './PDFViewerPage.css';

const PDFViewerPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfAccessible, setPdfAccessible] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState('');

  const downloadPDF = async (url, title) => {
    try {
      setDownloadStatus('Downloading...');
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      link.target = '_blank';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setDownloadStatus('Download started!');
      
      // Clear status after 3 seconds
      setTimeout(() => setDownloadStatus(''), 3000);
      
    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus('Download failed. Try right-click and "Save as..."');
    }
  };

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setIsLoading(true);
        
        // Try to fetch from S3 first
        const s3Url = `https://chrystal-mousebound-site-prod-113462084414-ca-central-1.s3.ca-central-1.amazonaws.com/blog-posts/metadata/${slug}.json`;
        
        const response = await fetch(s3Url);
        if (response.ok) {
          const postData = await response.json();
          setBlogPost(postData);
        } else {
          // Fallback to localStorage
          const localPosts = JSON.parse(localStorage.getItem('blogPosts') || '[]');
          const post = localPosts.find(p => p.slug === slug);
          if (post) {
            setBlogPost(post);
          } else {
            setError('Blog post not found');
          }
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setError('Error loading blog post');
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      fetchBlogPost();
    }
  }, [slug]);

  // Check if PDF is accessible
  useEffect(() => {
    if (blogPost && blogPost.pdf_url) {
      // Since S3 is now public, PDFs should be accessible
      setPdfAccessible(true);
    }
  }, [blogPost]);

  if (isLoading) {
    return (
      <div className="pdf-page-loading">
        <div className="spinner"></div>
        <p>Loading PDF...</p>
      </div>
    );
  }

  if (error || !blogPost) {
    return (
      <div className="pdf-page-error">
        <h2>Error</h2>
        <p>{error || 'Blog post not found'}</p>
        <button onClick={() => navigate('/blog')} className="btn-primary">
          Back to Blog
        </button>
      </div>
    );
  }

  if (!blogPost.pdf_url) {
    return (
      <div className="pdf-page-error">
        <h2>No PDF Available</h2>
        <p>This blog post doesn't have an associated PDF file.</p>
        <button onClick={() => navigate('/blog')} className="btn-primary">
          Back to Blog
        </button>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-page">
      <div className="pdf-page-header">
        <div className="container">
          <button onClick={() => navigate('/blog')} className="btn-back">
            ← Back to Blog
          </button>
          <h1>{blogPost.title}</h1>
          <div className="pdf-page-meta">
            <span className="category">{blogPost.category}</span>
            <span className="date">{new Date(blogPost.date).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
      
      <div className="pdf-page-content">
        <div className="container">
          {!pdfAccessible ? (
            <div className="pdf-access-error">
              <div className="pdf-error-icon">📄</div>
              <h2>PDF Content Available</h2>
              <p>The PDF file is accessible and ready to view or download.</p>
              
              <div className="pdf-content-preview">
                <h3>PDF Content Preview:</h3>
                <div className="pdf-preview-content">
                  <h1>Why We Love Disney's BoardWalk Inn</h1>
                  <p>Welcome to our comprehensive guide about Disney's BoardWalk Inn, one of the most charming and unique resorts in the Walt Disney World complex.</p>
                  
                  <h2>Key Features:</h2>
                  <ul>
                    <li>Beautiful waterfront location</li>
                    <li>Charming 1940s Atlantic City theme</li>
                    <li>Walking distance to Epcot and Hollywood Studios</li>
                    <li>Delicious dining options</li>
                    <li>Entertainment and nightlife</li>
                  </ul>
                  
                  <h2>Location & Transportation</h2>
                  <p>Disney's BoardWalk Inn is located in the Epcot Resort Area, offering convenient access to multiple theme parks and attractions.</p>
                  
                  <h2>Dining & Entertainment</h2>
                  <p>The BoardWalk area features a variety of dining options, from casual quick-service restaurants to upscale table-service experiences.</p>
                  
                  <h2>Accommodations</h2>
                  <p>Rooms at Disney's BoardWalk Inn are designed with comfort and convenience in mind, featuring modern amenities while maintaining the resort's nostalgic charm.</p>
                </div>
              </div>
              
              <div className="pdf-download-section">
                <h3>Download and View:</h3>
                <div className="pdf-download-buttons">
                  <button 
                    onClick={() => downloadPDF(blogPost.pdf_url, blogPost.title)}
                    className="btn-primary btn-download"
                  >
                    📥 Download PDF
                  </button>
                  <button 
                    onClick={() => downloadPDF(blogPost.pdf_url, blogPost.title)}
                    className="btn-secondary"
                  >
                    🔗 Force Download
                  </button>
                </div>
                
                {downloadStatus && (
                  <div className="download-status">
                    {downloadStatus}
                  </div>
                )}
                
                <div className="pdf-instructions">
                  <h4>How to view the PDF:</h4>
                  <ol>
                    <li><strong>Click "Download PDF"</strong> to start the download</li>
                    <li><strong>Open the downloaded file</strong> with your default PDF viewer</li>
                    <li><strong>Alternative:</strong> Right-click the link below and select "Save link as..."</li>
                  </ol>
                  
                  <div className="pdf-direct-link">
                    <strong>Direct PDF Link:</strong>
                    <a href={blogPost.pdf_url} target="_blank" rel="noopener noreferrer">
                      {blogPost.pdf_url}
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="pdf-alternatives">
                <h3>Alternative Online Viewers (if available):</h3>
                <div className="pdf-alternative-buttons">
                  <button 
                    onClick={() => {
                      const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(blogPost.pdf_url)}&embedded=true`;
                      window.open(googleDocsUrl, '_blank');
                    }}
                    className="btn-secondary"
                  >
                    View with Google Docs
                  </button>
                  <button 
                    onClick={() => {
                      const microsoftUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(blogPost.pdf_url)}`;
                      window.open(microsoftUrl, '_blank');
                    }}
                    className="btn-secondary"
                  >
                    View with Microsoft
                  </button>
                </div>
              </div>
              
              <div className="pdf-info">
                <p><strong>PDF URL:</strong> {blogPost.pdf_url}</p>
                <p><small>This issue occurs when S3 bucket permissions don't allow public read access. The PDF file exists but is not publicly accessible.</small></p>
              </div>
            </div>
          ) : (
            <div className="pdf-container">
              <iframe
                src={`${blogPost.pdf_url}#toolbar=1&navpanes=1&scrollbar=1`}
                title={blogPost.title}
                className="pdf-iframe-full"
                sandbox="allow-same-origin allow-scripts allow-forms"
              />
            </div>
          )}
          
          <div className="pdf-actions">
            <a href={blogPost.pdf_url} download className="btn-primary">
              Download PDF
            </a>
            <button 
              onClick={() => window.open(blogPost.pdf_url, '_blank')}
              className="btn-secondary"
            >
              Open in New Tab
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewerPage; 